import { useState } from "react";
import { useUrlSearch } from "@/lib/useUrlSearch";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PackageCheck, RefreshCw, Search } from "lucide-react";
import { formatINR } from "@/lib/utils";
import type { OrderRecord, OrderStatus } from "@/types";
import { OrderStatusBadge } from "@/components/orders/OrderStatusBadge";

const formatDate = (d?: string | null) => (d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");

const FILTERS: Array<{ key: string; label: string; status: string }> = [
  { key: "PENDING", label: "To dispatch", status: "OPEN,PARTIAL" },
  { key: "DISPATCHED", label: "Fully dispatched", status: "DISPATCHED" },
  { key: "CANCELLED", label: "Cancelled", status: "CANCELLED" },
  { key: "ALL", label: "All", status: "" },
];

export function OrdersPage() {
  const { error } = useToast();
  const [search, setSearch] = useUrlSearch();
  const [filter, setFilter] = useState("PENDING");

  const list = usePagedList<OrderRecord>({
    deps: [search, filter],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const qs = new URLSearchParams({ page: String(page), page_size: "30" });
      if (search.trim()) qs.set("search", search.trim());
      const status = FILTERS.find((f) => f.key === filter)?.status;
      if (status) qs.set("status", status);
      try {
        return unwrapPage<OrderRecord>(await api.get(`/api/SalesOrder/List?${qs}`));
      } catch (err: any) {
        error("Fetch Error", err.message || "Failed to load orders");
        return { rows: [], total: 0, page, page_size: 30 };
      }
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Orders &amp; Dispatch</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Buyers' purchase orders, what has gone out, and what is still pending. Make an order from a won deal.</p>
        </div>
        <Button variant="outline" size="sm" onClick={list.reload} disabled={list.loading} className="h-9 gap-1.5 self-start sm:self-auto">
          <RefreshCw className={`size-3.5 ${list.loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Main Workspace Card (Elevated #ffffff card on #efefef canvas) */}
      <div className="rounded-xl border border-border bg-card shadow-xs overflow-hidden">
        {/* Card Header: Search & Filter Pills */}
        <div className="p-3.5 sm:p-4 border-b border-border flex flex-col sm:flex-row gap-3 sm:items-center justify-between bg-card">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
            <Input placeholder="Search by order, PO number or buyer..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-9 text-xs bg-card" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${filter === f.key ? "bg-primary text-primary-foreground border-primary shadow-xs" : "bg-card hover:bg-muted border-border text-foreground"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {list.loading && list.rows.length === 0 ? (
          <div className="p-4"><TableSkeleton rows={6} columns={6} /></div>
        ) : (
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Buyer</TableHead>
                <TableHead>PO</TableHead>
                <TableHead className="text-right">Value (₹)</TableHead>
                <TableHead className="w-44">Dispatched</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.rows.map((o) => (
                <TableRow key={o.id}>
                  <TableCell>
                    <div className="flex items-start gap-2.5">
                      <div className="size-8 rounded-lg bg-teal-500/10 text-teal-600 dark:text-teal-400 flex items-center justify-center shrink-0">
                        <PackageCheck className="size-4" />
                      </div>
                      <div>
                        <Link to={`/orders/${o.id}`} className="font-semibold text-sm text-primary hover:underline">
                          {o.order_number}
                        </Link>
                        <div className="text-[11px] text-muted-foreground">
                          {formatDate(o.created_on)} · {o.line_count || 0} line{o.line_count === 1 ? "" : "s"}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div>{o.account?.name || "-"}</div>
                    {o.deal && (
                      <Link to={`/deals/${o.deal.id}`} className="text-xs text-muted-foreground hover:underline">
                        {o.deal.name}
                      </Link>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {o.po_number}
                    {o.po_date && <div className="text-[11px] text-muted-foreground">{formatDate(o.po_date)}</div>}
                  </TableCell>
                  <TableCell className="text-right text-sm whitespace-nowrap">
                    {formatINR(o.taxable_total)}
                    <div className="text-[11px] text-muted-foreground">before GST</div>
                  </TableCell>
                  <TableCell>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: `${o.progress_pct || 0}%` }} />
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">{o.progress_pct || 0}% sent</div>
                  </TableCell>
                  <TableCell>
                    <OrderStatusBadge status={o.order_status as OrderStatus} />
                  </TableCell>
                </TableRow>
              ))}
              {list.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground text-sm">
                    No orders here.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />
        </div>
      )}
      </div>
    </div>
  );
}
